/**
 * Shared style/display utility functions to replace nested ternaries.
 */

/** Performance color class based on value vs thresholds (high=good) */
export const perfColor = (value, high = 100, mid = 80) => {
  if (value >= high) return 'text-emerald-600';
  if (value >= mid) return 'text-amber-600';
  return 'text-rose-600';
};

/** Performance background class (for badges/pills) */
export const perfBg = (value, high = 100, mid = 80) => {
  if (value >= high) return 'bg-emerald-50 text-emerald-600';
  if (value >= mid) return 'bg-amber-50 text-amber-600';
  return 'bg-rose-50 text-rose-600';
};

/** Performance bar/fill color */
export const perfBar = (value, high = 100, mid = 80) => {
  if (value >= high) return 'bg-emerald-500';
  if (value >= mid) return 'bg-amber-500';
  return 'bg-rose-500';
};

/** Performance color for dark backgrounds */
export const perfColorDark = (value, high = 80, mid = 50) => {
  if (value >= high) return 'text-emerald-400';
  if (value >= mid) return 'text-amber-400';
  return 'text-rose-400';
};

/** Pick time badge class (low=good: <=8 green, <=15 amber, else red) */
export const pickTimeBadge = (minutes) => {
  if (minutes <= 8) return 'bg-emerald-50 text-emerald-600';
  if (minutes <= 15) return 'bg-amber-50 text-amber-600';
  return 'bg-rose-50 text-rose-600';
};

/** Picks-per-hour badge class (high=good: >=8 green, >=4 amber, else red) */
export const picksHrBadge = (rate) => {
  if (rate >= 8) return 'bg-emerald-50 text-emerald-600';
  if (rate >= 4) return 'bg-amber-50 text-amber-600';
  return 'bg-rose-50 text-rose-600';
};

/** 6S audit score color (4-tier) */
export const auditScoreColor = (score) => {
  if (score >= 4) return 'text-emerald-600';
  if (score >= 3) return 'text-blue-600';
  if (score >= 2) return 'text-amber-600';
  return 'text-rose-600';
};

/** Progress bar color from a BRAND palette (returns a hex color value) */
export const progressBarColor = (value, brand, t1 = 100, t2 = 70, t3 = 40) => {
  if (value >= t1) return brand.cyan;
  if (value >= t2) return brand.emerald;
  if (value >= t3) return brand.amber;
  return brand.rose;
};

/** Goals progress bar class */
export const goalBarClass = (value) => {
  if (value >= 100) return 'bg-cyan-500';
  if (value >= 70) return 'bg-emerald-500';
  if (value >= 40) return 'bg-amber-500';
  return 'bg-rose-500';
};

/** Checklist filter label */
export const checklistFilterLabel = (status) => {
  const labels = {
    all: 'All Checklists',
    pending: 'Pending Checklists',
    in_progress: 'In Progress Checklists',
    completed: 'Completed Checklists',
  };
  return labels[status] || 'All Checklists';
};

/** Empty state message for list pages (guest / search / default) */
export const emptyStateMsg = (isGuest, search, defaultMsg) => {
  if (isGuest) return 'Guest accounts cannot view active items';
  if (search) return 'Try adjusting your search terms';
  return defaultMsg;
};

/** DMAIC phase bar color based on status */
export const phaseBarCls = (status, phaseColor) => {
  if (status === 'completed') return phaseColor;
  if (status === 'in_progress') return `${phaseColor} opacity-50`;
  return 'bg-muted';
};
export const flowStepStyle = (step) => {
  const type = (step.type || step.step_type || '')?.toLowerCase();
  if (type === 'decision') return { bg: 'bg-rose-500/5 border-rose-500/20', iconColor: 'text-rose-400 fill-rose-400/20' };
  if (type === 'start') return { bg: 'bg-blue-500/5 border-blue-500/20', iconColor: 'text-blue-400 fill-blue-400/20' };
  if (type === 'end') return { bg: 'bg-emerald-500/5 border-emerald-500/20', iconColor: 'text-emerald-400 fill-emerald-400/20' };
  return { bg: 'bg-muted/20 border-muted/40', iconColor: 'text-muted-foreground' };
};
